import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { employeesAPI } from '../services/api';
import {
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  MapPin,
  Save,
  Eye,
  EyeOff,
  Edit,
  Briefcase,
  Clock,
  ChevronDown,
  ChevronUp,
  LogOut,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const Profile = () => {
  const navigate = useNavigate();
  const { updateUser, user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [packageSectionOpen, setPackageSectionOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const tenantInfo = user?.tenantInfo;
  const subscriptionLabel = tenantInfo?.packageLifecycle?.status || 'unlinked';

  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await employeesAPI.getProfile();
      setProfile(response.data);
      setProfileForm({
        name: response.data.name || '',
        email: response.data.email || '',
        phoneNumber: response.data.phoneNumber || '',
        address: response.data.address || '',
        emergencyContact: response.data.emergencyContact || '',
        emergencyPhone: response.data.emergencyPhone || '',
      });
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      const response = await employeesAPI.updateProfile(profileForm);
      setProfile(response.data);
      updateUser(response.data);
      setIsEditing(false);
      toast.success('Profile updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await employeesAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsChangingPassword(false);
      toast.success('Password updated');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    }
  };

  const cancelEdit = () => {
    if (profile) {
      setProfileForm({
        name: profile.name || '',
        email: profile.email || '',
        phoneNumber: profile.phoneNumber || '',
        address: profile.address || '',
        emergencyContact: profile.emergencyContact || '',
        emergencyPhone: profile.emergencyPhone || '',
      });
    }
    setIsEditing(false);
  };

  const cancelPasswordChange = () => {
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setIsChangingPassword(false);
  };

  if (isLoading) {
    return (
      <div className="py-16 text-center text-gray-500 text-sm">Loading profile…</div>
    );
  }

  if (!profile) {
    return (
      <div className="panel text-center py-12">
        <User className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <h1 className="text-base font-semibold text-gray-900">Profile not found</h1>
        <p className="text-subtle text-sm mt-1">Unable to load your profile information.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="page-title">Profile</h1>
          <p className="page-subtitle">Personal information and account security.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 self-start sm:self-auto">
          {!isEditing && (
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              className="btn-outline"
            >
              <Edit className="w-3.5 h-3.5 mr-1.5" />
              Edit profile
            </button>
          )}
          <button
            type="button"
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="btn-outline"
          >
            <LogOut className="w-3.5 h-3.5 mr-1.5" />
            Sign out
          </button>
        </div>
      </div>

      {/* Summary card */}
      <div className="panel flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gray-900 text-white flex items-center justify-center text-base font-semibold shrink-0">
          {profile.name?.[0]?.toUpperCase() || 'U'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-gray-900">{profile.name}</h2>
            <span className="badge badge-secondary capitalize">{profile.role}</span>
            <span className="badge badge-secondary">
              {profile.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="text-subtle text-sm mt-0.5">
            {profile.position} · {profile.department}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
          <span className="px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50">
            ID: {profile.employeeId}
          </span>
          {profile.dateOfJoining && (
            <span className="px-2.5 py-1 rounded-md border border-gray-200 bg-gray-50">
              Joined {format(new Date(profile.dateOfJoining), 'MMM yyyy')}
            </span>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: Personal Info */}
        <div className="lg:col-span-2 panel !p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Personal information</h2>
          </div>

          <div className="p-4 md:p-5">
            {!isEditing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoRow icon={User} label="Full name" value={profile.name} />
                <InfoRow icon={Mail} label="Email" value={profile.email} />
                <InfoRow icon={Phone} label="Phone" value={profile.phoneNumber || 'Not provided'} />
                <InfoRow icon={Building} label="Department" value={profile.department} />
                <InfoRow icon={Briefcase} label="Position" value={profile.position} />
                <InfoRow
                  icon={Calendar}
                  label="Date of joining"
                  value={
                    profile.dateOfJoining
                      ? format(new Date(profile.dateOfJoining), 'MMM dd, yyyy')
                      : 'Not provided'
                  }
                />
                {profile.shift && <InfoRow icon={Clock} label="Shift" value={profile.shift} />}
                <InfoRow icon={MapPin} label="Address" value={profile.address || 'Not provided'} />
              </div>
            ) : (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormInput
                    label="Full name"
                    name="name"
                    value={profileForm.name}
                    onChange={handleProfileInputChange}
                    required
                  />
                  <FormInput
                    label="Email"
                    name="email"
                    type="email"
                    value={profileForm.email}
                    onChange={handleProfileInputChange}
                    required
                  />
                  <FormInput
                    label="Phone number"
                    name="phoneNumber"
                    value={profileForm.phoneNumber}
                    onChange={handleProfileInputChange}
                  />
                  <FormInput
                    label="Emergency contact"
                    name="emergencyContact"
                    value={profileForm.emergencyContact}
                    onChange={handleProfileInputChange}
                  />
                  <FormInput
                    label="Emergency phone"
                    name="emergencyPhone"
                    value={profileForm.emergencyPhone}
                    onChange={handleProfileInputChange}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                  <textarea
                    name="address"
                    value={profileForm.address}
                    onChange={handleProfileInputChange}
                    rows="2"
                    className="input !h-auto py-2 resize-none"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button type="button" onClick={cancelEdit} className="btn-outline">
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    <Save className="w-3.5 h-3.5 mr-1.5" />
                    Save changes
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Right: Security + Package */}
        <div className="space-y-4">
          <div className="panel !p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Security</h2>
            </div>
            <div className="p-4">
              {!isChangingPassword ? (
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium text-gray-900">Password</div>
                    <div className="text-xs text-gray-500">Update your account password</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsChangingPassword(true)}
                    className="btn-outline shrink-0"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className="space-y-3">
                  <PasswordInput
                    label="Current password"
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordInputChange}
                    show={showCurrentPassword}
                    toggle={() => setShowCurrentPassword(!showCurrentPassword)}
                  />
                  <PasswordInput
                    label="New password"
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordInputChange}
                    show={showNewPassword}
                    toggle={() => setShowNewPassword(!showNewPassword)}
                    hint="Minimum 6 characters"
                  />
                  <PasswordInput
                    label="Confirm new password"
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordInputChange}
                    show={showConfirmPassword}
                    toggle={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                  <div className="flex justify-end gap-2 pt-1">
                    <button type="button" onClick={cancelPasswordChange} className="btn-outline">
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      Update
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {tenantInfo && (
            <div className="panel !p-0 overflow-hidden">
              <button
                type="button"
                onClick={() => setPackageSectionOpen((open) => !open)}
                className="w-full px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2 text-left"
              >
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-gray-900">Subscription</h2>
                  <p className="text-xs text-gray-500 truncate">
                    {tenantInfo.tenant?.name || tenantInfo.tenant?.tenantId || 'Organization'}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="badge badge-secondary capitalize">{subscriptionLabel}</span>
                  {packageSectionOpen ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </button>

              <div className="p-4 space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-subtle">Plan</span>
                  <span className="font-medium text-gray-900">
                    {tenantInfo.linkedPackage?.name || 'Standard'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-subtle">Base</span>
                  <span className="text-gray-800">
                    {tenantInfo.pricingCurrency}{' '}
                    {Number(tenantInfo.flatAmount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-subtle">Per employee</span>
                  <span className="text-gray-800">
                    {tenantInfo.pricingCurrency}{' '}
                    {Number(tenantInfo.perEmployeeAmount || 0).toLocaleString()}
                  </span>
                </div>
                {packageSectionOpen && (
                  <div className="pt-2 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                    <div>
                      Start:{' '}
                      {tenantInfo.packageStartDate
                        ? format(new Date(tenantInfo.packageStartDate), 'dd MMM yyyy')
                        : 'Not set'}
                    </div>
                    <div>
                      End:{' '}
                      {tenantInfo.packageEndDate
                        ? format(new Date(tenantInfo.packageEndDate), 'dd MMM yyyy')
                        : 'Not set'}
                    </div>
                    {tenantInfo.pricingNote ? <div>{tenantInfo.pricingNote}</div> : null}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <div className="w-7 h-7 bg-gray-100 rounded-md flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="w-3.5 h-3.5 text-gray-500" />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-medium text-gray-900 break-words">{value}</div>
    </div>
  </div>
);

const FormInput = ({ label, name, type = 'text', value, onChange, required = false }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">
      {label} {required && '*'}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="input"
    />
  </div>
);

const PasswordInput = ({ label, name, value, onChange, show, toggle, hint }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label} *</label>
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        required
        minLength={name === 'newPassword' ? 6 : undefined}
        className="input pr-9"
      />
      <button
        type="button"
        onClick={toggle}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

export default Profile;
